import React, { useState, useEffect } from "react";
import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Typography,
	Tabs,
	Spin,
	Alert,
} from "antd";
import {
	EyeOutlined,
	BookOutlined,
	CalendarOutlined,
	UserOutlined,
	DollarOutlined,
} from "@ant-design/icons";
import useAxios from "../../utilities/useAxios";
import { formatDate, formatCurrency } from "../../utilities/Utils";
import PageTitle from "../PageTitle";
import { useNavigate } from "react-router-dom";
import PageHeader from "../common/PageHeader";

const { Text } = Typography;
const { TabPane } = Tabs;

const AdminCourses = () => {
	const navigate = useNavigate();
	const [courses, setCourses] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		fetchCourses();
	}, []);

	const fetchCourses = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/cma/admin/courses/",
			);
			setCourses(response.data);
			setError(null);
		} catch (err) {
			console.error("Error fetching courses:", err);
			setError("강의 목록을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const getCourseStatus = (course) => {
		const today = new Date();
		const endDate = new Date(course.end_date);

		if (endDate < today) {
			return {
				status: "finished",
				color: "default",
				text: "종료",
			};
		} else {
			return {
				status: "progress",
				color: "processing",
				text: "진행중",
			};
		}
	};

	const courseColumns = [
		{
			title: "강의명",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<Space direction="vertical" size={0}>
					<Text strong>{text}</Text>
					<Text
						type="secondary"
						style={{ fontSize: "12px" }}>
						<BookOutlined />{" "}
						{record.book_title}
					</Text>
				</Space>
			),
		},
		{
			title: "기간",
			key: "period",
			render: (_, record) => (
				<Space direction="vertical" size={0}>
					<Text>
						<CalendarOutlined />{" "}
						{formatDate(record.start_date)}{" "}
						~ {formatDate(record.end_date)}
					</Text>
					<Tag
						color={
							getCourseStatus(record)
								.color
						}>
						{getCourseStatus(record).text}
					</Tag>
				</Space>
			),
		},
		{
			title: "수강생",
			key: "enrollments",
			render: (_, record) => (
				<Space direction="vertical" size={0}>
					<Text>
						<UserOutlined /> 총{" "}
						{record.total_enrollments}명
					</Text>
					<Text type="secondary">
						결제완료:{" "}
						{record.paid_enrollments}명
					</Text>
				</Space>
			),
		},
		{
			title: "수강료",
			key: "fees",
			render: (_, record) => (
				<Space direction="vertical" size={0}>
					<Text>
						<DollarOutlined /> 정식:{" "}
						{formatCurrency(
							record.fee_official,
						)}
					</Text>
					<Text type="secondary">
						청강:{" "}
						{formatCurrency(
							record.fee_auditing,
						)}
					</Text>
				</Space>
			),
		},
		{
			title: "유형",
			dataIndex: "type",
			key: "type",
			render: (type) => (
				<Tag
					color={
						type === "온라인"
							? "blue"
							: "green"
					}>
					{type}
				</Tag>
			),
		},
		{
			title: "자세히 보기",
			key: "actions",
			render: (_, record) => (
				<Button
					type="primary"
					icon={<EyeOutlined />}
					onClick={() =>
						navigate(
							`/cma/admin/courses/${record.id}`,
						)
					}>
					자세히 보기
				</Button>
			),
		},
	];

	const inProgressCourses = courses.filter(
		(course) => !course.is_finished,
	);
	const finishedCourses = courses.filter((course) => course.is_finished);

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="page">
			<PageTitle title="기독사관학교 - 강의 관리" />
			<PageHeader title="강의 관리" />
			{error && (
				<Alert
					message="오류"
					description={error}
					type="error"
					showIcon
					className="mb-3"
				/>
			)}

			<Card className="surface-card">
				<Tabs defaultActiveKey="progress">
					<TabPane
						tab={`진행중 (${inProgressCourses.length})`}
						key="progress">
						<Table
							columns={courseColumns}
							dataSource={
								inProgressCourses
							}
							rowKey="id"
							pagination={{
								pageSize: 10,
								showSizeChanger: true,
								showQuickJumper: true,
								showTotal: (
									total,
									range,
								) =>
									`${range[0]}-${range[1]} / 총 ${total}개`,
							}}
						/>
					</TabPane>
					<TabPane
						tab={`종료된 강의 (${finishedCourses.length})`}
						key="finished">
						<Table
							columns={courseColumns}
							dataSource={
								finishedCourses
							}
							rowKey="id"
							pagination={{
								pageSize: 10,
								showSizeChanger: true,
								showQuickJumper: true,
								showTotal: (
									total,
									range,
								) =>
									`${range[0]}-${range[1]} / 총 ${total}개`,
							}}
						/>
					</TabPane>
				</Tabs>
			</Card>
		</div>
	);
};

export default AdminCourses;
